/**
 * quoteTitle — a quote names itself from its content until someone renames it
 * (BUILDER-FLOW-01 / QUOTE-AUTONAME, Seth 2026-10-30: "let them rename the quote if they'd like
 * by clicking on it"). The dispatcher's question on a call-back is "which quote?" — the name
 * answers it: "Repair · Rooftop Unit 581JP08D", "Lennox Elite · Furnace & A/C". A hand-typed
 * name wins and stays (`title_is_auto` = false).
 */
import { deriveSeries, type ClassifiedEquip } from "@/lib/equipment";
import type { Quote, QuoteOptionFull } from "./quotes";

/** The placeholders every door has ever written — treated as "not named yet". */
const DEFAULT_TITLE_RE = /^(new quote|new system quote|system proposal|new system(\s.*)?|repair options(\s+—.*)?)$/i;

export function isAutoTitle(q: Pick<Quote, "title" | "title_is_auto">): boolean {
  return !!q.title_is_auto || !(q.title ?? "").trim() || DEFAULT_TITLE_RE.test((q.title ?? "").trim());
}

/** "581JP08D180A2A" → "581JP08D" — the part of a model a person reads out loud. */
const shortModel = (model: string | null | undefined): string => {
  const m = (model ?? "").trim().toUpperCase();
  if (!m) return "";
  const head = m.split(/[-\s]/)[0];
  return head.length > 10 ? head.slice(0, 10) : head;
};

const KIND_WORD: Record<string, string> = {
  furnace: "Furnace", ac: "A/C", heat_pump: "Heat Pump", "heat-pump": "Heat Pump", boiler: "Boiler", coil: "Coil",
  air_handler: "Air Handler", "air-handler": "Air Handler", mini_split: "Mini-Split", "mini-split": "Mini-Split",
  water_heater: "Water Heater", "water-heater": "Water Heater", rooftop: "Rooftop Unit", garage_heater: "Garage Heater",
};
const SERIES_WORD: Record<string, string> = { signature: "Signature", elite: "Elite", merit: "Merit", armstrong: "Armstrong" };

export function autoQuoteTitle(args: {
  quote: Pick<Quote, "quote_type" | "work_units" | "recommended_option_id">;
  options: QuoteOptionFull[];
  /** The property's classified equipment (the same record the Working On rows read). */
  units: ClassifiedEquip[];
  /** Equipment-kind keys on this quote's catalog (install lines), key → label. */
  kindLabel?: (kind: string) => string | null;
  address?: string | null;
}): string {
  const { quote, options, units, kindLabel, address } = args;
  const street = (address ?? "").trim().replace(/,.*$/, "");
  const isService = (quote.quote_type || "install") === "service";
  if (isService) {
    const picked = new Set(quote.work_units ?? []);
    const worked = units.filter((u) => picked.has(u.counter));
    if (worked.length === 1) {
      const u = worked[0];
      const m = shortModel(u.model);
      return `Repair · ${u.categoryLabel}${m ? ` ${m}` : ""}`;
    }
    if (worked.length > 1) {
      const kinds = [...new Set(worked.map((u) => u.categoryLabel))];
      return `Repair · ${kinds.length <= 2 ? kinds.join(" & ") : `${worked.length} units`}`;
    }
    return `Repair${street ? ` · ${street}` : ""}`;
  }
  // Install: the advisor's pick, else the first package that holds equipment.
  const equipKinds = new Set(["furnace", "ac", "heat_pump", "heat-pump", "boiler", "air_handler", "air-handler", "mini_split", "mini-split", "water_heater", "water-heater", "rooftop", "garage_heater"]);
  const withEquip = (o: QuoteOptionFull) => o.lines.filter((l) => l.role !== "recommended" && equipKinds.has(l.kind));
  const rec = options.find((o) => o.id === quote.recommended_option_id && withEquip(o).length > 0)
    ?? options.find((o) => withEquip(o).length > 0);
  if (rec) {
    const lines = withEquip(rec);
    const brand = (rec.brand ?? lines.find((l) => l.manufacturer)?.manufacturer ?? "").trim();
    const series = lines.map((l) => deriveSeries(l.manufacturer ?? brand, l.model)).find((s) => s !== "other") ?? null;
    const kinds = [...new Set(lines.map((l) => kindLabel?.(l.kind) ?? KIND_WORD[l.kind] ?? l.kind))];
    const what = kinds.length <= 2 ? kinds.join(" & ") : `${kinds.length} units`;
    const who = [brand, series ? SERIES_WORD[series] : null].filter(Boolean).join(" ");
    return who ? `${who} · ${what}` : `New system · ${what}`;
  }
  return `New system${street ? ` · ${street}` : ""}`;
}
